import { useState, useEffect } from "react";
import axios from "axios";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Building2, Mail, Phone, MapPin, BadgeCheck,
  Package, FileText, Receipt, IndianRupee, TrendingUp, Download,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import DashboardLayout from "../../components/layouts/DashboardLayout";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const fmtDate = (s) => {
  if (!s) return "-";
  const d = new Date(s);
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};

const paxStr = (a, c) => {
  const adults = Number(a || 0); const children = Number(c || 0);
  if (!adults && !children) return "-";
  return children > 0 ? `${adults}A + ${children}C` : `${adults}A`;
};

const statusBadge = (s) => {
  const map = {
    confirmed: "bg-emerald-100 text-emerald-700",
    pending: "bg-amber-100 text-amber-700",
    cancelled: "bg-rose-100 text-rose-700",
    quoted: "bg-indigo-100 text-indigo-700",
    booked: "bg-emerald-100 text-emerald-700",
    full: "bg-emerald-100 text-emerald-700",
    partial: "bg-amber-100 text-amber-700",
  };
  return map[(s || "").toLowerCase()] || "bg-gray-100 text-gray-700";
};

const KpiTile = ({ label, value, accent, sub }) => (
  <Card className="bg-white border-gray-200 shadow-sm">
    <CardContent className="p-4">
      <p className="text-[11px] uppercase tracking-wide text-gray-500">{label}</p>
      <p className={`text-xl font-bold tabular-nums mt-1 ${accent || "text-gray-900"}`}>{value}</p>
      {sub && <p className="text-[11px] text-gray-400 mt-0.5">{sub}</p>}
    </CardContent>
  </Card>
);

const AgentDetail = () => {
  const { id } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("bookings");

  useEffect(() => {
    if (!id || !token) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/agents/${id}/detail`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setData(res.data);
      } catch (e) {
        console.error("Failed to load agent detail", e);
      } finally {
        setLoading(false);
      }
    })();
  }, [id, token]);

  if (loading) {
    return (
      <DashboardLayout type="admin">
        <div className="space-y-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-24 rounded-md bg-gray-100 animate-pulse" />
          ))}
        </div>
      </DashboardLayout>
    );
  }

  if (!data) {
    return (
      <DashboardLayout type="admin">
        <div className="text-center py-20 text-gray-500">Agent not found.</div>
      </DashboardLayout>
    );
  }

  const { agent, kpis, bookings, quotes, agent_payments } = data;
  const tabs = [
    { id: "bookings", label: "Bookings", icon: Package, count: bookings.length },
    { id: "quotes", label: "Quotes", icon: FileText, count: quotes.length },
    { id: "payments", label: "Payments", icon: Receipt, count: agent_payments.length },
  ];

  // ── Derived total pax across all bookings ─────────────────────
  const totalPax = bookings.reduce((s, b) => s + Number(b.pax_adults || 0) + Number(b.pax_children || 0), 0);
  const totalAdults = bookings.reduce((s, b) => s + Number(b.pax_adults || 0), 0);
  const totalChildren = bookings.reduce((s, b) => s + Number(b.pax_children || 0), 0);
  const totalPaxLabel = totalChildren > 0 ? `${totalAdults}A + ${totalChildren}C` : `${totalAdults}A`;

  // ── CSV Export ─────────────────────────────────────────────────
  const exportCSV = () => {
    const rows = [
      ["Booking #","Guest Name","Pax","Destination","Nights","Travel Date","Return Date","Booking Amt","Paid","Balance","GST Amt","Profit","Status","Payment Status"],
      ...bookings.map(b => {
        const payStatus = b.balance_amount <= 0 && b.amount_paid > 0 ? "full" : b.amount_paid > 0 ? "partial" : "pending";
        return [
          b.booking_number || b.id.slice(0,8).toUpperCase(),
          b.guest_name || b.client_name || "-",
          paxStr(b.pax_adults, b.pax_children),
          b.destination || "-",
          b.nights ? `${b.nights} Nights` : "-",
          fmtDate(b.travel_date),
          fmtDate(b.return_date),
          b.dmc_total || 0,
          b.amount_paid || 0,
          b.balance_amount > 0 ? b.balance_amount : 0,
          b.gst_amount || 0,
          b.net_profit || 0,
          b.status || "-",
          payStatus,
        ];
      })
    ];
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g,'""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${agent.company_name || agent.name}_bookings.csv`; a.click();
    URL.revokeObjectURL(url);
    toast.success("Excel (CSV) downloaded");
  };

  const exportPDF = () => {
    const w = window.open("", "_blank");
    const rows = bookings.map(b => {
      const payStatus = b.balance_amount <= 0 && b.amount_paid > 0 ? "Full" : b.amount_paid > 0 ? "Partial" : "Pending";
      return `<tr>
        <td>#${b.booking_number || b.id.slice(0,8).toUpperCase()}</td>
        <td>${b.guest_name || b.client_name || "-"}<br/><small>${paxStr(b.pax_adults, b.pax_children)}</small></td>
        <td>${b.destination || "-"}<br/><small>${b.nights ? b.nights + " Nights" : ""}</small></td>
        <td>${fmtDate(b.travel_date)}<br/><small>${fmtDate(b.return_date)}</small></td>
        <td style="text-align:right">₹${Number(b.dmc_total||0).toLocaleString("en-IN")}</td>
        <td style="text-align:right">₹${Number(b.amount_paid||0).toLocaleString("en-IN")}</td>
        <td style="text-align:right">${b.balance_amount > 0 ? "₹"+Number(b.balance_amount).toLocaleString("en-IN") : "Nil"}</td>
        <td style="text-align:right">₹${Number(b.gst_amount||0).toLocaleString("en-IN")}</td>
        <td style="text-align:right">₹${Number(b.net_profit||0).toLocaleString("en-IN")}</td>
        <td>${b.status || "-"}</td>
        <td>${payStatus}</td>
      </tr>`;
    }).join("");
    w.document.write(`<!DOCTYPE html><html><head><title>${agent.company_name || agent.name} — Booking Report</title>
    <style>body{font-family:Arial,sans-serif;font-size:11px;padding:20px}h1{color:#059669;font-size:16px;margin-bottom:4px}p{color:#6b7280;margin:0 0 12px}table{width:100%;border-collapse:collapse}th{background:#059669;color:#fff;padding:6px 8px;text-align:left;font-size:10px}td{padding:5px 8px;border-bottom:1px solid #e5e7eb}tr:nth-child(even)td{background:#f9fafb}small{color:#9ca3af}</style>
    </head><body>
    <h1>${agent.company_name || agent.name} — Agent Booking Report</h1>
    <p>Total Bookings: ${bookings.length} | Total Pax: ${totalPaxLabel} | Booking Amt: ₹${Number(kpis.total_booking_amount).toLocaleString("en-IN")} | Paid: ₹${Number(kpis.total_paid).toLocaleString("en-IN")} | Balance: ₹${Number(kpis.total_balance).toLocaleString("en-IN")}</p>
    <table><thead><tr><th>Booking #</th><th>Guest / Pax</th><th>Destination / Nights</th><th>Travel / Return</th><th>Booking Amt</th><th>Paid</th><th>Balance</th><th>GST</th><th>Profit</th><th>Status</th><th>Payment</th></tr></thead>
    <tbody>${rows}</tbody></table></body></html>`);
    w.document.close(); w.focus(); w.print();
  };

  return (
    <DashboardLayout type="admin">
      <div className="space-y-6" data-testid="agent-detail">
        {/* Back link + Download buttons */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <Link
            to="/admin"
            className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800"
            data-testid="agent-detail-back"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Dashboard
          </Link>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              onClick={exportCSV}
              data-testid="download-excel-btn"
            >
              <Download className="w-3.5 h-3.5" /> Download Excel
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5 border-rose-300 text-rose-700 hover:bg-rose-50"
              onClick={exportPDF}
              data-testid="download-pdf-btn"
            >
              <Download className="w-3.5 h-3.5" /> Download PDF
            </Button>
          </div>
        </div>

        {/* Agent profile card */}
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardContent className="p-6">
            <div className="flex flex-wrap items-start justify-between gap-6">
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 rounded-xl bg-emerald-100 flex items-center justify-center">
                  <Building2 className="w-7 h-7 text-emerald-700" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h1 className="text-2xl font-bold text-gray-900">{agent.company_name || agent.name}</h1>
                    {agent.is_gst_registered && (
                      <Badge className="bg-emerald-100 text-emerald-700 border-0">
                        <BadgeCheck className="w-3.5 h-3.5 mr-1" /> GST
                      </Badge>
                    )}
                    <Badge className={`${statusBadge(agent.status)} border-0 capitalize`}>{agent.status || "active"}</Badge>
                  </div>
                  <p className="text-gray-500 text-sm mt-0.5">{agent.name}</p>
                  <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-gray-600">
                    {agent.email && (<span className="inline-flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> {agent.email}</span>)}
                    {agent.phone && (<span className="inline-flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" /> {agent.phone}</span>)}
                    {(agent.city || agent.state) && (<span className="inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> {[agent.city, agent.state].filter(Boolean).join(", ")}</span>)}
                  </div>
                  {(agent.gstin || agent.pan_number) && (
                    <div className="flex flex-wrap gap-4 mt-2 text-xs text-gray-500">
                      {agent.gstin && (<span>GSTIN: <span className="font-mono text-gray-700">{agent.gstin}</span></span>)}
                      {agent.pan_number && (<span>PAN: <span className="font-mono text-gray-700">{agent.pan_number}</span></span>)}
                    </div>
                  )}
                </div>
              </div>
              <div className="text-right text-xs text-gray-500">
                <p>Joined</p>
                <p className="text-gray-800 font-medium">{fmtDate(agent.created_at)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* KPI tiles */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="agent-kpis">
          <KpiTile label="Bookings" value={`${kpis.total_bookings} (${totalPaxLabel})`} accent="text-indigo-700" />
          <KpiTile label="Booking Amount" value={inr(kpis.total_booking_amount)} accent="text-gray-900" />
          <KpiTile label="Received" value={inr(kpis.total_paid)} accent="text-emerald-700" />
          <KpiTile label="Balance" value={inr(kpis.total_balance)} accent="text-rose-600" />
          <KpiTile label="GST" value={inr(kpis.total_gst)} accent="text-amber-700" />
          <KpiTile
            label="Net Profit"
            value={
              <span className="inline-flex items-center gap-1">
                <TrendingUp className="w-4 h-4" /> {inr(kpis.total_profit)}
                <span className="text-xs text-gray-500 font-normal ml-1">({kpis.profit_margin}%)</span>
              </span>
            }
            accent="text-emerald-700"
          />
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2">
          {tabs.map((t) => {
            const active = tab === t.id;
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all ${
                  active
                    ? "bg-emerald-50 text-emerald-700 shadow-sm border border-emerald-200"
                    : "bg-white text-gray-600 hover:bg-gray-50 border border-gray-200"
                }`}
                data-testid={`agent-tab-${t.id}`}
              >
                <Icon className="w-4 h-4" />
                {t.label}
                <span className={`text-xs rounded-full px-2 py-0.5 ${active ? "bg-emerald-200/60 text-emerald-800" : "bg-gray-100 text-gray-600"}`}>{t.count}</span>
              </button>
            );
          })}
        </div>

        {/* Tab content */}
        {tab === "bookings" && (
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardContent className="p-0">
              {bookings.length === 0 ? (
                <p className="text-center text-gray-500 py-12">No bookings for this agent yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50/70">
                      <tr className="text-left text-[11px] uppercase tracking-wide text-gray-500 border-b border-gray-200">
                        <th className="px-4 py-3">Booking #</th>
                        <th className="px-4 py-3">Guest / Pax</th>
                        <th className="px-4 py-3">Destination / Nights</th>
                        <th className="px-4 py-3">Travel / Return</th>
                        <th className="px-4 py-3 text-right">Booking Amt</th>
                        <th className="px-4 py-3 text-right">Paid</th>
                        <th className="px-4 py-3 text-right">Balance</th>
                        <th className="px-4 py-3 text-right">GST Amt</th>
                        <th className="px-6 py-3 text-right">Profit</th>
                        <th className="px-6 py-3">Status</th>
                        <th className="px-6 py-3">Payment</th>
                        <th className="px-4 py-3"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {bookings.map((b) => {
                        const payStatus = b.balance_amount <= 0 && b.amount_paid > 0
                          ? "full"
                          : b.amount_paid > 0
                          ? "partial"
                          : "pending";
                        return (
                          <tr key={b.id} className="hover:bg-gray-50/50" data-testid={`agent-booking-${b.id}`}>
                            <td className="px-4 py-3 font-mono text-xs text-emerald-700">#{b.booking_number || b.id.slice(0,8).toUpperCase()}</td>
                            <td className="px-4 py-3">
                              <p className="text-gray-800 font-medium">{b.guest_name || b.client_name || "-"}</p>
                              <p className="text-xs text-gray-400 mt-0.5">{paxStr(b.pax_adults, b.pax_children)}</p>
                            </td>
                            <td className="px-4 py-3">
                              <p className="text-gray-700">{b.destination || "-"}</p>
                              {b.nights > 0 && <p className="text-xs text-gray-400 mt-0.5">{b.nights} Nights</p>}
                            </td>
                            <td className="px-4 py-3">
                              <p className="text-gray-600">{fmtDate(b.travel_date)}</p>
                              {b.return_date && <p className="text-xs text-gray-400 mt-0.5">{fmtDate(b.return_date)}</p>}
                            </td>
                            <td className="px-4 py-3 text-right text-indigo-700 font-semibold tabular-nums">{inr(b.dmc_total)}</td>
                            <td className="px-4 py-3 text-right text-emerald-700 tabular-nums">{inr(b.amount_paid)}</td>
                            <td className={`px-4 py-3 text-right tabular-nums ${b.balance_amount > 0 ? "text-rose-600" : "text-emerald-700"}`}>{b.balance_amount > 0 ? inr(b.balance_amount) : "Nil"}</td>
                            <td className="px-4 py-3 text-right text-amber-600 tabular-nums">{b.gst_amount > 0 ? inr(b.gst_amount) : "-"}</td>
                            <td className="px-6 py-3 text-right text-emerald-700 font-semibold tabular-nums">{inr(b.net_profit)}</td>
                            <td className="px-6 py-3"><Badge className={`${statusBadge(b.status)} border-0 capitalize whitespace-nowrap`}>{b.status || "-"}</Badge></td>
                            <td className="px-6 py-3"><Badge className={`${statusBadge(payStatus)} border-0 capitalize whitespace-nowrap`}>{payStatus}</Badge></td>
                            <td className="px-4 py-3 text-right">
                              <Button variant="ghost" size="sm" onClick={() => navigate(`/admin/bookings/${b.id}`)}>View</Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {tab === "quotes" && (
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardContent className="p-0">
              {quotes.length === 0 ? (
                <p className="text-center text-gray-500 py-12">No quotes for this agent yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50/70">
                      <tr className="text-left text-[11px] uppercase tracking-wide text-gray-500 border-b border-gray-200">
                        <th className="px-4 py-3">Quote #</th>
                        <th className="px-4 py-3">Guest</th>
                        <th className="px-4 py-3">Destination</th>
                        <th className="px-4 py-3">Travel</th>
                        <th className="px-4 py-3 text-right">Quote Amt</th>
                        <th className="px-4 py-3">Status</th>
                        <th className="px-4 py-3"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {quotes.map((q) => (
                        <tr key={q.id} className="hover:bg-gray-50/50" data-testid={`agent-quote-${q.id}`}>
                          <td className="px-4 py-3 font-mono text-xs text-emerald-700">#{q.quote_number || q.id.slice(0,8).toUpperCase()}</td>
                          <td className="px-4 py-3 text-gray-800">{q.guest_name || q.client_name || "-"}</td>
                          <td className="px-4 py-3 text-gray-700">{q.destination || "-"}</td>
                          <td className="px-4 py-3 text-gray-600">{fmtDate(q.travel_date)}</td>
                          <td className="px-4 py-3 text-right text-indigo-700 font-semibold tabular-nums">{inr(q.quote_amount)}</td>
                          <td className="px-4 py-3"><Badge className={`${statusBadge(q.status)} border-0 capitalize`}>{q.status || "-"}</Badge></td>
                          <td className="px-4 py-3 text-right">
                            <Button variant="ghost" size="sm" onClick={() => navigate(`/admin/quote-builder/${q.id}`)}>Open</Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {tab === "payments" && (
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardContent className="p-0">
              {agent_payments.length === 0 ? (
                <p className="text-center text-gray-500 py-12">No agent payments received yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50/70">
                      <tr className="text-left text-[11px] uppercase tracking-wide text-gray-500 border-b border-gray-200">
                        <th className="px-4 py-3">Date</th>
                        <th className="px-4 py-3">Booking</th>
                        <th className="px-4 py-3">Destination</th>
                        <th className="px-4 py-3 text-right">Amount</th>
                        <th className="px-4 py-3">Mode</th>
                        <th className="px-4 py-3">Reference</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {agent_payments.map((p) => (
                        <tr key={p.id} className="hover:bg-gray-50/50" data-testid={`agent-payment-${p.id}`}>
                          <td className="px-4 py-3 text-gray-700">{fmtDate(p.payment_date)}</td>
                          <td className="px-4 py-3 font-mono text-xs text-emerald-700">#{p.booking_ref}</td>
                          <td className="px-4 py-3 text-gray-700">{p.destination || "-"}</td>
                          <td className="px-4 py-3 text-right text-emerald-700 font-semibold tabular-nums inline-flex items-center justify-end gap-1">
                            <IndianRupee className="w-3.5 h-3.5" />{Number(p.amount_received || 0).toLocaleString("en-IN")}
                          </td>
                          <td className="px-4 py-3 text-gray-600 uppercase text-xs">{p.payment_mode || "-"}</td>
                          <td className="px-4 py-3 text-xs text-gray-500 font-mono">{p.transaction_id || p.reference_number || "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AgentDetail;
